import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { supabase } from "../lib/supabase";

const SOCIALS = [
  {
    name: "Google",
    color: "from-red-500 to-yellow-400",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 48 48"><g><path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3C34.7 32.1 29.8 35 24 35c-6.1 0-11.3-4.1-13.1-9.6-1.8-5.5.2-11.5 5.1-14.7 4.9-3.2 11.5-2.6 15.7 1.4l6.2-6.2C34.1 2.7 29.2 1 24 1 12.4 1 3 10.4 3 22c0 11.6 9.4 21 21 21 10.5 0 19.2-7.7 20.7-17.7.2-1.1.3-2.2.3-3.3 0-1.2-.1-2.3-.4-3.5z"/><path fill="#34A853" d="M6.3 14.6l6.6 4.8C14.2 16.1 18.7 13 24 13c3.1 0 6 .9 8.3 2.6l6.2-6.2C34.1 2.7 29.2 1 24 1 15.2 1 7.6 6.7 6.3 14.6z"/><path fill="#FBBC05" d="M24 43c5.5 0 10.5-1.8 14.4-4.9l-6.7-5.5C29.7 34.7 26.9 35.5 24 35.5c-5.7 0-10.5-3.7-12.2-8.7l-6.6 5.1C7.6 39.3 15.2 45 24 45z"/><path fill="#EA4335" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.6 5.6-6.6 7.1l6.7 5.5C39.5 41.2 43.6 36.7 43.6 20.5z"/></g></svg>
    )
  }
];

export default function Login() {
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      nav('/browse');
    }
  }, [isAuthenticated, authLoading, nav]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name } }
        });
        if (signUpError) throw signUpError;
        nav("/browse");
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (signInError) throw signInError;
        nav("/browse");
      }
    } catch (err) {
      setError(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleSocial(name) {
    setError("");
    setLoading(true);
    try {
      if (name === "Google") {
        console.log('Starting Google OAuth...');
        const { data, error } = await supabase.auth.signInWithOAuth({ 
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        });
        
        console.log('OAuth response:', { data, error });
        
        if (error) {
          console.error('OAuth error:', error);
          throw error;
        }
        
        console.log('OAuth initiated successfully');
        // The user will be redirected by Supabase
      }
    } catch (err) {
      console.error('Social login error:', err);
      setError(err.message || "Social login failed");
      setLoading(false);
    }
  }

  // Show loading if auth is still loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-3xl">👶</span>
          </div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="w-full max-w-md min-h-[520px] flex flex-col justify-center bg-white/80 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/40 animate-fade-in-up">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-3">
            <span className="text-white text-3xl">👶</span>
          </div>
          <h2 className="text-2xl font-bold gradient-text mb-2">BabyXchange</h2>
          <p className="text-sm text-slate-600 text-center mb-4 italic">
            {t('tagline')}
          </p>
          <div className="flex w-full mb-2 mt-2">
            <button
              className={`flex-1 py-2 rounded-l-xl font-semibold transition-all duration-200 ${mode === "signin" ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow" : "bg-white text-slate-700 border border-slate-200"}`}
              onClick={() => setMode("signin")}
            >
              {t('auth.signIn')}
            </button>
            <button
              className={`flex-1 py-2 rounded-r-xl font-semibold transition-all duration-200 ${mode === "register" ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow" : "bg-white text-slate-700 border border-slate-200"}`}
              onClick={() => setMode("register")}
            >
              {t('auth.signUp')}
            </button>
          </div>
          <h1 className="text-responsive-xl font-bold gradient-text mb-1">
            {mode === "signin" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-slate-600 text-sm">
            {mode === "signin" ? "Sign in to Baby Exchange" : "Register to start exchanging!"}
          </p>
        </div>
        {/* Social Login Buttons */}
        <div className="space-y-3 mb-6">
          {SOCIALS.map(s => (
            <button
              key={s.name}
              type="button"
              onClick={() => handleSocial(s.name)}
              className={`w-full flex items-center justify-center gap-3 py-2.5 rounded-xl font-semibold shadow-md bg-gradient-to-r ${s.color} text-white hover:scale-[1.02] transition-transform duration-150`}
              disabled={loading}
            >
              {s.icon}
              Sign in with {s.name}
            </button>
          ))}
        </div>
        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="mx-4 text-xs text-slate-400">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        {/* Email/Password Form */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          {mode === "register" && (
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
          )}
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="you@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <div className="text-red-600 text-sm text-center">{error}</div>}
          <button
            type="submit"
            className="w-full btn-primary text-lg py-3 mt-2 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? (mode === "signin" ? "Signing in..." : "Registering...") : (mode === "signin" ? "Sign In" : "Register")}
          </button>
        </form>
        <div className="mt-8 text-center text-xs text-slate-500">
          {mode === "signin"
            ? "Sign in with Google or email."
            : "Register with Google or email. Check your inbox for confirmation if required."}
        </div>
      </div>
    </div>
  );
}
